import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Heart, Shield, Users, ArrowRight } from 'lucide-react';
import './About.css';

export const About = () => {
  const values = [
    {
      icon: <Heart size={32} />,
      title: 'Personalized Service',
      description: 'Every celebration is unique. We tailor our services to match your vision and exceed your expectations.'
    },
    {
      icon: <Award size={32} />,
      title: 'Excellence in Hospitality',
      description: 'With years of experience hosting memorable events, we bring professionalism and warmth to every celebration.'
    },
    {
      icon: <Shield size={32} />,
      title: 'Trusted & Reliable',
      description: 'From planning to execution, we ensure every detail is perfect so you can enjoy your special day worry-free.'
    },
    {
      icon: <Users size={32} />,
      title: 'Family Atmosphere',
      description: 'We treat every guest like family, creating warm and welcoming experiences that leave lasting impressions.'
    }
  ];

  return (
    <div className="about-page">
      <section className="about-hero">
        <div className="container">
          <span className="section-subtitle">Our Story</span>
          <h1 className="hero-medium">The Green Valley, Agra</h1>
          <p className="body-large mt-24">
            The crown jewel of Shastripuram – West Agra’s most cherished wedding and celebration venue
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding">
        <div className="container">
          <div className="two-column-layout">
            <div className="image-column">
              <img 
                src="https://customer-assets.emergentagent.com/job_luxweddings/artifacts/j8ycg17v_Screenshot%202025-10-12%20220639.png" 
                alt="Vibrant Mehndi Setup at The Green Valley"
                className="feature-image"
              />
            </div>
            <div className="content-column">
              <h2 className="heading-1 mb-24">Where Grandeur Meets Warmth</h2>
              <p className="body-large mb-24">
                Nestled in the heart of Shastripuram, West Agra, The Green Valley has been transforming special days 
                into lifelong memories. Our venue combines the elegance of modern design with the warmth of traditional hospitality.
              </p>
              <p className="body-regular mb-24">
                We believe that every celebration deserves a perfect setting. That’s why we’ve created a venue that offers 
                versatility without compromising on beauty – from our grand lawn with signature fountains to our elegant 
                banquet halls and stunning poolside area.
              </p>
              <p className="body-regular">
                What sets us apart is not just our world-class infrastructure, but our commitment to personalized service. 
                Our experienced event coordination team works closely with you to bring your vision to life, handling every 
                detail with care and precision.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding values-section">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">Our Values</span>
            <h2 className="heading-1">What We Stand For</h2>
          </div>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card">
                <div className="value-icon">{value.icon}</div>
                <h3 className="heading-3 mb-16">{value.title}</h3>
                <p className="body-regular">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Advantage */}
      <section className="section-padding location-advantage">
        <div className="container">
          <div className="two-column-layout">
            <div className="content-column">
              <span className="section-subtitle">Location</span>
              <h2 className="heading-1 mb-24">Shastripuram, West Agra</h2>
              <p className="body-large mb-24">
                Strategically located in West Agra, our venue offers the perfect balance of accessibility and exclusivity.
              </p>
              <ul className="feature-list">
                <li>Easy access from major routes and highways</li>
                <li>Away from city bustle, ensuring peaceful celebrations</li>
                <li>Wide approach roads for smooth guest arrival</li>
                <li>Ample secure parking with valet service</li>
                <li>Scenic surroundings with tranquil ambience</li>
                <li>Close to major landmarks in Agra</li>
              </ul>
              <div className="location-details mt-32">
                <p className="body-regular mb-16">
                  <strong>Address:</strong><br />
                  100 Ft Road, Near Krishna Indraprastha Colony<br />
                  Shastripuram, Agra – 282007
                </p>
                <a href="https://www.google.com/maps/search/?api=1&query=The+Green+Valley+Shastripuram+Agra" 
                   target="_blank" 
                   rel="noopener noreferrer" 
                   className="btn-primary">
                  Get Directions
                  <ArrowRight size={20} style={{ marginLeft: '8px' }} />
                </a>
              </div>
            </div>
            <div className="image-column">
              <img 
                src="https://customer-assets.emergentagent.com/job_luxweddings/artifacts/j6o6ntwb_IMG-20251012-WA0021.jpg" 
                alt="Night View of The Green Valley"
                className="feature-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Infrastructure */}
      <section className="section-padding infrastructure-section">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">Infrastructure</span>
            <h2 className="heading-1">World-Class Facilities</h2>
            <p className="body-large mt-16">
              Every element of our venue is designed to ensure flawless execution of your celebration
            </p>
          </div>
          <div className="infrastructure-grid">
            <div className="infra-card">
              <h3 className="heading-3 mb-16">Venue Spaces</h3>
              <ul className="feature-list-simple">
                <li>Grand Lawn (up to 1000 guests)</li>
                <li>Air-Conditioned Banquet Halls</li>
                <li>Poolside Venue (up to 300 guests)</li>
                <li>Multiple staging areas</li>
              </ul>
            </div>
            <div className="infra-card">
              <h3 className="heading-3 mb-16">Accommodation</h3>
              <ul className="feature-list-simple">
                <li>8 Premium AC Rooms</li>
                <li>4 Deluxe AC Rooms</li>
                <li>Bridal & Groom Suites</li>
                <li>Modern furnishings & amenities</li>
              </ul>
            </div>
            <div className="infra-card">
              <h3 className="heading-3 mb-16">Services</h3>
              <ul className="feature-list-simple">
                <li>Professional Valet Parking</li>
                <li>Trained Security Personnel</li>
                <li>Event Coordination Team</li>
                <li>24/7 Power Backup</li>
              </ul>
            </div>
            <div className="infra-card">
              <h3 className="heading-3 mb-16">Aesthetics</h3>
              <ul className="feature-list-simple">
                <li>Signature Fountains</li>
                <li>Architectural Lighting</li>
                <li>Flexible Décor Options</li>
                <li>Photography-Friendly Spaces</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="glass-card cta-card">
            <h2 className="hero-medium text-center">Experience The Green Valley</h2>
            <p className="body-large text-center mt-24 mb-32">
              Visit us to experience the elegance and warmth that makes The Green Valley Agra’s premier celebration venue
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn-primary">Schedule a Visit</Link>
              <a href="https://wa.me/918826372788?text=Hi!%20I'd%20like%20to%20learn%20more%20about%20The%20Green%20Valley." 
                 target="_blank" 
                 rel="noopener noreferrer" 
                 className="btn-secondary">
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
